import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { CheckList } from './CheckList';
import {
  describeAction,
  describeProgress,
  formatSeconds,
  formatTime,
  overrideBlockers,
} from './cutover-view';
import { CutoverAuditLog } from './CutoverAuditLog';
import { MoveBackDialog } from './MoveBackDialog';
import { NewUserBackendSwitch } from './NewUserBackendSwitch';
import { NextStepDialog } from './NextStepDialog';
import { useCutoverStatusQuery } from './useCutover';

import type { CutoverStatus, CutoverStepLabel } from './cutover.types';

type OpenDialog =
  | {
      kind: 'next-step';
      step: number;
      target: CutoverStepLabel;
    }
  | { kind: 'move-back'; step: number };

const BREAK_GLASS_RUNBOOK_URL =
  'https://github.com/littlebirdai/littlebird/blob/main/ops/k8s/ts-backend/README.md#break-glass-the-typescript-api-is-down';

// How long the current step has been live, as information: no step waits
// for time to pass
function StepLiveSummary({ status }: { status: CutoverStatus }) {
  if (status.step_live_since === null) {
    return (
      <p className="text-sm text-muted-foreground">No users have moved yet.</p>
    );
  }
  return (
    <p className="text-sm">
      Live since{' '}
      <time dateTime={status.step_live_since}>
        {formatTime(status.step_live_since)}
      </time>
      , for {formatSeconds(status.step_live_seconds ?? 0)}.
    </p>
  );
}

function RunningMove({ status }: { status: CutoverStatus }) {
  const action = status.active_action;
  if (action === null) return null;
  // A user whose move fails is tried again in the next batch, so `failed`
  // counts attempts and `moved` can reach `planned` while `failed` is not 0
  const percent =
    action.planned === null || action.planned === 0
      ? 0
      : Math.min(100, Math.round((action.moved / action.planned) * 100));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {describeAction(action)}: {action.status}
        </CardTitle>
        <CardDescription>
          Asked by {action.actor} at {formatTime(action.created_at)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div
          role="progressbar"
          aria-label="Move progress"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
        <p>{describeProgress(action)}.</p>
      </CardContent>
    </Card>
  );
}

export function CutoverPage() {
  const { data: status, isLoading, error } = useCutoverStatusQuery();
  // What a dialog was opened for. It keeps that target while the status
  // refreshes, so a confirmation never moves to another step under the reader
  const [openDialog, setOpenDialog] = useState<OpenDialog | null>(null);
  const openNextStep = (current: CutoverStatus) => {
    if (current.next_step_label === null) return;
    setOpenDialog({
      kind: 'next-step',
      step: current.step,
      target: current.next_step_label,
    });
  };
  const hasStepChanged =
    status !== undefined &&
    openDialog !== null &&
    status.step !== openDialog.step;
  const isMoveRunning = Boolean(status?.active_action);
  // Changes when an action starts or ends, so the audit log rereads then and
  // never keeps a row that still says running
  const auditLogVersion = [
    status?.active_action?.id,
    status?.last_action?.id,
    status?.last_action?.status,
  ].join(':');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-4">
          <div>
            <CardTitle>Backend cutover</CardTitle>
            <CardDescription>
              Moves users from the Python backend to the TypeScript backend in
              three steps: 10%, 50% and 100%.
            </CardDescription>
          </div>
          <p className="text-sm text-muted-foreground">
            This page needs the TypeScript API. If it is down, use the{' '}
            <a
              className="underline"
              href={BREAK_GLASS_RUNBOOK_URL}
              target="_blank"
              rel="noreferrer"
            >
              break-glass Job
            </a>{' '}
            to move everyone back to Python.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <p className="text-muted-foreground">Loading the cutover...</p>
          )}
          {error && <p className="text-sm text-destructive">{error.message}</p>}
          {status && (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Current step</p>
                <p className="text-3xl font-semibold">
                  {status.step === 0 ? 'None' : status.step_label}
                </p>
                <p className="text-sm text-muted-foreground">
                  {status.moved_users} users moved by the cutover.{' '}
                  {status.held_users > 0 &&
                    `${status.held_users} users are kept on Python.`}
                </p>
                {status.last_action?.finished_at && (
                  <p className="text-sm">
                    Last action: {describeAction(status.last_action)},{' '}
                    {status.last_action.status} at{' '}
                    {formatTime(status.last_action.finished_at)}.
                  </p>
                )}
                {status.unfinished_step_label && (
                  <p className="text-sm text-amber-600">
                    The move to {status.unfinished_step_label} did not finish.
                    Moving to it again moves the users it left.
                  </p>
                )}
              </div>
              <StepLiveSummary status={status} />
              <div className="flex flex-wrap items-center gap-2">
                {status.next_step_label && (
                  <Button
                    disabled={isMoveRunning}
                    onClick={() => openNextStep(status)}
                  >
                    <ArrowUpCircle />
                    Next step: {status.next_step_label}
                  </Button>
                )}
                <Button
                  variant="outline"
                  disabled={isMoveRunning}
                  onClick={() =>
                    setOpenDialog({ kind: 'move-back', step: status.step })
                  }
                >
                  <ArrowDownCircle />
                  Move back
                </Button>
                {isMoveRunning && (
                  <span className="text-sm text-muted-foreground">
                    A move is running. Wait for it to end.
                  </span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <NewUserBackendSwitch />

      {status && (
        <>
          <RunningMove status={status} />
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preflight</CardTitle>
                <CardDescription>
                  Run now. Every check must pass before any step.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CheckList
                  checks={status.preflight}
                  emptyText="The service reported no preflight checks."
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Health checks</CardTitle>
                <CardDescription>
                  Each check against its limit. A failing check holds the next
                  step unless you override it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CheckList
                  checks={status.health_checks}
                  emptyText="The service has no health checks yet."
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audit log</CardTitle>
          <CardDescription>
            Every cutover action, refusals included, newest first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CutoverAuditLog
            isMoveRunning={isMoveRunning}
            version={auditLogVersion}
          />
        </CardContent>
      </Card>

      {openDialog?.kind === 'next-step' && (
        <NextStepDialog
          target={openDialog.target}
          // Read from each status refresh, so a refusal for a check that
          // started failing brings up the override field
          blockers={status ? overrideBlockers(status) : []}
          hasStepChanged={hasStepChanged}
          onClose={() => setOpenDialog(null)}
        />
      )}
      {openDialog?.kind === 'move-back' && (
        <MoveBackDialog
          step={openDialog.step}
          hasStepChanged={hasStepChanged}
          onClose={() => setOpenDialog(null)}
        />
      )}
    </div>
  );
}
